import axios from 'axios';
import { CONFIG } from '@/config/Config';
import { addSessionId } from '@/core/utils/httpHeaders';
import { getAccountId } from '@/core/utils/extractCookieId';

const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

interface RepliersCacheEntry {
  data: AccountReplier[];
  timestamp: number;
}

const getRepliersCacheKey = (): string => `warroom_account_repliers_cache:${getAccountId()}`;

const readRepliersFromCache = (): AccountReplier[] | null => {
  try {
    const cacheKey = getRepliersCacheKey();
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as Partial<RepliersCacheEntry>;
    const { data, timestamp } = parsed;
    const isValidEntry = Array.isArray(data)
      && data.every((item) => typeof item?.id === 'string' && item.id.length > 0 && typeof item?.name === 'string' && item.name.length > 0)
      && typeof timestamp === 'number' && Number.isFinite(timestamp);
    if (!isValidEntry) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    if (Date.now() - timestamp > CACHE_DURATION_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

const writeRepliersToCache = (data: AccountReplier[]): void => {
  try {
    const entry: RepliersCacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(getRepliersCacheKey(), JSON.stringify(entry));
  } catch (error) {
    console.error('Failed to write repliers cache to localStorage:', error);
  }
};

/** Clears the cached repliers for the current account - exposed mainly for tests. */
export const clearRepliersCache = (): void => {
  try {
    localStorage.removeItem(getRepliersCacheKey());
  } catch {
    // ignore
  }
};

interface RawReplier {
  id?: string | number;
  user_id?: string | number;
  name?: string;
  label?: string;
  username?: string;
  email?: string;
}

export interface AccountReplier {
  id: string;
  name: string;
}

const getRepliersSource = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === 'object') {
    const objectPayload = payload as { repliers?: unknown[]; data?: { repliers?: unknown[] } };

    if (Array.isArray(objectPayload.repliers)) return objectPayload.repliers;
    if (Array.isArray(objectPayload.data?.repliers)) return objectPayload.data.repliers;
  }

  return [];
};

const mapRawReplier = (raw: unknown): AccountReplier | null => {
  if (typeof raw === 'string') {
    const value = raw.trim();
    if (!value) return null;

    return {
      id: value,
      name: value,
    };
  }

  if (!raw || typeof raw !== 'object') return null;

  const replier = raw as RawReplier;
  const name = String(
    replier.name ?? replier.label ?? replier.username ?? replier.email ?? ''
  ).trim();

  if (!name) return null;

  const rawId = replier.id ?? replier.user_id ?? replier.email ?? replier.username ?? name;
  const id = String(rawId).trim();

  if (!id) return null;

  return { id, name };
};

export async function fetchAccountRepliers(): Promise<AccountReplier[]> {
  const accountHeader = CONFIG.cookieOrParam(CONFIG.AUTH.ACC_ID) ?? undefined;
  const universeHeader = CONFIG.cookieOrParam(CONFIG.AUTH.UNI_ID) ?? undefined;
  const accountId = accountHeader?.split('|')[0];

  if (!accountId) {
    console.warn('accId is undefined, skipping fetchAccountRepliers');
    return [];
  }

  const cached = readRepliersFromCache();
  if (cached) return cached;

  const url = `${CONFIG.getGatewayUrl()}upms-legacy/${CONFIG.getVersion()}/account/${accountId}/repliers`;

  const headers = addSessionId({
    accept: 'application/json',
  });

  if (accountHeader) {
    headers['stilingue-account-id'] = accountHeader;
  }

  if (universeHeader) {
    headers['stilingue-universe-id'] = universeHeader;
  }

  const response = await axios.get(url, { headers });
  const rawItems = getRepliersSource(response.data);

  const mapped = rawItems
    .map(mapRawReplier)
    .filter(Boolean) as AccountReplier[];

  const dedupedById = new Map<string, AccountReplier>();
  mapped.forEach((item) => {
    if (!dedupedById.has(item.id)) {
      dedupedById.set(item.id, item);
    }
  });

  const repliers = Array.from(dedupedById.values());
  writeRepliersToCache(repliers);
  return repliers;
}
